/** A Google Places pick split into the fields trips and billing store. */
export interface PlaceAddress {
  formatted: string;
  street: string;
  number: string;
  postcode: string;
  town: string;
  country: string;
  missing: ('street' | 'number' | 'postcode' | 'town')[];
}

type Part = {long_name: string, short_name: string, types: string[]};

// GoogleMapsService installs window.gm_authFailure before it injects the Maps script, so a
// rejected key is flagged on window.__gmAuthFailed no matter when this module evaluates.
export function placesAvailable(): boolean {
  return !(window as any).__gmAuthFailed && !!(window as any).google?.maps?.places?.Autocomplete;
}

/** Google's suggestion list is appended to body; an open one has rows and is not hidden. */
export function suggestionsOpen(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>('.pac-container'))
    .some(el => el.style.display !== 'none' && el.childElementCount > 0);
}

/** Null when Google resolved nothing, e.g. Enter on typed text with no suggestion chosen. */
export function parsePlace(place: {address_components?: Part[], formatted_address?: string, name?: string}): PlaceAddress | null {
  const parts = place?.address_components;
  if (!parts?.length) { return null; }
  const get = (type: string, short = false) => {
    const part = parts.find(p => p.types.includes(type));
    return part ? (short ? part.short_name : part.long_name) : '';
  };
  const base = get('street_number'), sub = get('subpremise');
  const a = {
    street: get('route'),
    number: base && sub ? base + (/^[a-z]+$/i.test(sub) ? sub : '-' + sub) : base,
    postcode: get('postal_code'),
    town: get('locality') || get('postal_town'),
  };
  const missing = (['street', 'number', 'postcode', 'town'] as const).filter(k => !a[k]);
  return {formatted: place.formatted_address || place.name || '', ...a, country: get('country', true), missing};
}

export function streetLine(a: PlaceAddress): string {
  return [a.street, a.number].filter(Boolean).join(' ');
}
